import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Check, X, ChevronDown, ChevronRight, Package, Shield, Zap, Save, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform`;

const MODULE_LABELS = {
  hiretrack: { label: 'HireTrack', desc: 'Recruitment pipeline — positions, resumes, interviews, closures', icon: '🎯' },
  recruiteriq: { label: 'RecruiterIQ', desc: 'AI-powered recruitment tools — JD gen, resume screening, insights', icon: '🧠' },
  nextmove: { label: 'NextMove', desc: 'Predictive analytics — pipeline health, forecasts, demand surge', icon: '📊' },
  hrms: { label: 'HRMS', desc: 'Human Resource Management — attendance, leaves, policies, exit', icon: '🏢' },
  talentvault: { label: 'TalentVault', desc: 'Candidate database & AI search', icon: '🗄' },
  knowledge_hub: { label: 'Knowledge Hub', desc: 'Document repository & knowledge base', icon: '📚' },
  client_team: { label: 'Client & Team Mgmt', desc: 'Companies, teams, recruiters, targets, revenue', icon: '🤝' },
  administration: { label: 'Administration', desc: 'User management, bulk upload, activity logs, assets', icon: '⚙' },
  organization: { label: 'Organization', desc: 'Subscription, branding, privacy, roles', icon: '🏗' },
  analytics: { label: 'Analytics', desc: 'AI usage analytics & reporting', icon: '📈' },
};

const SUB_LABELS = {
  ccr: 'Calling Report', 'ccr-summary': 'CCR Summary', positions: 'Positions', resumes: 'Resumes',
  interviews: 'Interviews', closures: 'Closures', joinings: 'Joinings', 'open-positions': 'Open Positions',
  'weekly-priorities': 'Priority Tracker', 'job-postings': 'Job Postings',
  'recruiter-iq': 'RecruiterIQ AI', nextmove: 'NextMove Analytics',
  attendance: 'Attendance', 'leave-management': 'Leave Management', 'employee-directory': 'Employee Directory',
  'onboarding-form': 'Onboarding', performance: 'Performance', policies: 'Policies',
  'my-assets': 'My Assets', lms: 'LMS / Training', 'talent-buzz': 'Talent Buzz',
  polls: 'Polls & Surveys', resignations: 'Resignations', clearance: 'Clearance',
  assets: 'Assets Tracking', fnf: 'F&F Settlement', 'exit-surveys': 'Exit Surveys', 'exit-reports': 'Exit Reports',
  'talent-vault': 'TalentVault', 'knowledge-hub': 'Knowledge Hub',
  companies: 'Companies', teams: 'Teams', recruiters: 'Recruiters', analytics: 'Client Analytics',
  targets: 'Targets', revenue: 'Revenue Summary',
  'user-management': 'User Management', announcements: 'Announcements', 'bulk-upload': 'Bulk Upload',
  'activity-log': 'Activity Log', 'asset-management': 'Asset Management', finance: 'Revenue & Expenses',
  'ai-analytics': 'AI Analytics', instructions: 'How to Use',
  subscription: 'Subscription', invoices: 'Invoices', branding: 'Branding', usage: 'Usage Dashboard',
  'privacy-settings': 'Privacy & Data Rights', 'custom-css': 'Custom CSS', roles: 'Roles',
};

export default function SuperAdminModuleControl() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [enabledModules, setEnabledModules] = useState([]);
  const [disabledSubs, setDisabledSubs] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [moduleSubmodules, setModuleSubmodules] = useState({});
  const [planDefault, setPlanDefault] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [original, setOriginal] = useState({ modules: [], subs: [] });

  const token = localStorage.getItem('superAdminToken');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  const fetchData = useCallback(async () => {
    try {
      const tenantRes = await axios.get(`${API}/tenants?search=${slug}`, { headers: getHeaders() });
      const t = tenantRes.data.find(t => t.slug === slug);
      if (!t) { toast.error('Tenant not found'); navigate('/super-admin/tenants'); return; }
      setTenant(t);
      const modRes = await axios.get(`${API}/tenants/${t.id}/modules`, { headers: getHeaders() });
      setEnabledModules(modRes.data.enabled_modules || []);
      setDisabledSubs(modRes.data.disabled_submodules || []);
      setAllModules(modRes.data.all_modules || []);
      setModuleSubmodules(modRes.data.module_submodules || {});
      setPlanDefault(modRes.data.plan_default || []);
      setOriginal({ modules: modRes.data.enabled_modules || [], subs: modRes.data.disabled_submodules || [] });
    } catch (e) {
      toast.error('Failed to load module configuration');
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const modsChanged = JSON.stringify([...enabledModules].sort()) !== JSON.stringify([...original.modules].sort());
    const subsChanged = JSON.stringify([...disabledSubs].sort()) !== JSON.stringify([...original.subs].sort());
    setHasChanges(modsChanged || subsChanged);
  }, [enabledModules, disabledSubs, original]);

  const toggleModule = (mod) => {
    setEnabledModules(prev =>
      prev.includes(mod) ? prev.filter(m => m !== mod) : [...prev, mod]
    );
  };

  const toggleSubmodule = (sub) => {
    setDisabledSubs(prev =>
      prev.includes(sub) ? prev.filter(s => s !== sub) : [...prev, sub]
    );
  };

  const toggleExpand = (mod) => {
    setExpandedModules(prev => ({ ...prev, [mod]: !prev[mod] }));
  };

  const resetToPlanDefaults = () => {
    setEnabledModules([...planDefault]);
    setDisabledSubs([]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/tenants/${tenant.id}/modules`, {
        modules: enabledModules,
        disabled_submodules: disabledSubs,
      }, { headers: getHeaders() });
      toast.success('Module configuration saved');
      setOriginal({ modules: [...enabledModules], subs: [...disabledSubs] });
      setHasChanges(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
    </div>
  );

  const isPlanModule = (mod) => planDefault.includes(mod);
  const isAddon = (mod) => enabledModules.includes(mod) && !planDefault.includes(mod);

  return (
    <div data-testid="module-control-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Module Access Control
            </h1>
            <p className="text-sm text-slate-500">
              {tenant?.name} &middot; Plan: <span className="font-bold text-violet-600 uppercase">{tenant?.plan}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={resetToPlanDefaults} data-testid="reset-defaults-btn"
            className="text-slate-600">
            <RotateCcw size={14} className="mr-1" /> Reset to Plan Defaults
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!hasChanges || saving} data-testid="save-modules-btn"
            className={hasChanges ? 'bg-violet-600 hover:bg-violet-700 text-white' : ''}>
            <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
          <Shield size={16} /> You have unsaved changes. Click "Save Changes" to apply.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center text-sm">
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
          <p className="text-2xl font-extrabold text-violet-700">{enabledModules.length}</p>
          <p className="text-violet-600 font-medium">Modules Enabled</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <p className="text-2xl font-extrabold text-emerald-700">{planDefault.length}</p>
          <p className="text-emerald-600 font-medium">Plan Included</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-2xl font-extrabold text-blue-700">{enabledModules.filter(m => !planDefault.includes(m)).length}</p>
          <p className="text-blue-600 font-medium">Add-on Modules</p>
        </div>
      </div>

      <div className="space-y-3">
        {allModules.map(mod => {
          const info = MODULE_LABELS[mod] || { label: mod, desc: '', icon: '📦' };
          const isEnabled = enabledModules.includes(mod);
          const isExpanded = expandedModules[mod];
          const subs = moduleSubmodules[mod] || [];
          const enabledSubCount = subs.filter(s => !disabledSubs.includes(s)).length;

          return (
            <div key={mod} data-testid={`module-card-${mod}`}
              className={`border rounded-lg overflow-hidden transition-colors ${isEnabled ? 'border-violet-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-70'}`}>
              <div className="flex items-center gap-3 p-4">
                <button onClick={() => toggleModule(mod)} data-testid={`toggle-module-${mod}`}
                  className={`w-10 h-6 rounded-full relative transition-colors ${isEnabled ? 'bg-violet-600' : 'bg-slate-300'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${isEnabled ? 'left-[18px]' : 'left-0.5'}`} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{info.icon}</span>
                    <span className="font-bold text-slate-900">{info.label}</span>
                    {isPlanModule(mod) && <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded uppercase">Plan</span>}
                    {isAddon(mod) && <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase">Add-on</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{info.desc}</p>
                </div>
                {isEnabled && subs.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{enabledSubCount}/{subs.length} active</span>
                    <button onClick={() => toggleExpand(mod)} data-testid={`expand-${mod}`}
                      className="p-1 hover:bg-slate-100 rounded">
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  </div>
                )}
              </div>

              {isEnabled && isExpanded && subs.length > 0 && (
                <div className="border-t border-slate-100 bg-slate-50/50 p-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {subs.map(sub => {
                      const isSubEnabled = !disabledSubs.includes(sub);
                      return (
                        <button key={sub} onClick={() => toggleSubmodule(sub)} data-testid={`toggle-sub-${sub}`}
                          className={`flex items-center gap-2 px-3 py-2 rounded text-sm text-left transition-colors ${
                            isSubEnabled ? 'bg-white border border-violet-200 text-slate-800' : 'bg-slate-100 border border-slate-200 text-slate-400 line-through'
                          }`}>
                          {isSubEnabled ? <Check size={14} className="text-violet-600 shrink-0" /> : <X size={14} className="text-slate-400 shrink-0" />}
                          <span>{SUB_LABELS[sub] || sub}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
